<template>
  <div class="portfolio">
    <portfolio-chart class="portfolio__chart" />
    <div class="portfolio__group">
      <ul class="portfolio__pills">
        <li v-if="chartData.clpData">
          <pill :deletable="false" :backgroundColor="clpColor" color="#666"
            >{{ t('portfolio.forecastedConsumption') }}</pill
          >
        </li>
        <li v-if="chartData.priceDatakWh">
          <pill :deletable="false" :backgroundColor="priceColor" color="#fff"
            >{{ t('portfolio.pricePerKwh') }}</pill
          >
        </li>
        <li v-for="cube in cubes" :key="cube">
          <pill
            :deletable="false"
            :backgroundColor="getPowerColor(cube)"
            color="#fff"
          >
            {{ getCubeNickName(cube) }}
          </pill>
        </li>
      </ul>
      <ul class="portfolio__list">
        <li>
          <span class="portfolio__list-number">
            {{ communitySharePercent }}%
          </span>
          <span>{{ t('portfolio.community') }}</span>
        </li>
        <li>
          <span class="portfolio__list-number">
            {{ 100 - communitySharePercent }}%
          </span>
          <span>{{ t('portfolio.residualPower') }}</span>
        </li>
        <li>
          <span class="portfolio__list-number"> {{ averagePrice }} ct </span>
          <span>{{ t('portfolio.estimatedEnergyPrice') }}</span>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
  import { defineComponent, computed } from 'vue'
  import ChartControls from '@/components/ChartControls.vue'
  import StyledSelect from '@/components/StyledSelect.vue'
  import Pill from '@/components/Pill.vue'
  import PortfolioChart from '@/components/efriends/PortfolioChart.vue'
  import { usePortfolioChartColors } from '@/components/efriends/portfolioChartColors.js'
  import { useStrings } from '@/composables/useStrings.ts'
  import { usePortfolioStore } from '@/stores/usePortfolioStore'
  import { useMapStore } from '@/stores/useMapStore'

  export default defineComponent({
    components: { ChartControls, StyledSelect, PortfolioChart, Pill },
    setup() {
      const { t } = useStrings()
      const portfolioStore = usePortfolioStore()
      const mapStore = useMapStore()
      const { clpColor, priceColor, getPowerColor } = usePortfolioChartColors()

      portfolioStore.loadPortfolioSession()
      portfolioStore.loadPortfolioCategories()

      const loadProfiles = computed(() => portfolioStore.profileCategories)
      const loadProfile = computed({
        get: () => portfolioStore.loadProfile,
        set: (profile) => portfolioStore.setLoadProfile(profile),
      })

      const consumption = computed({
        get: () => portfolioStore.consumption,
        set: (value) => portfolioStore.setPortfolioConsumption(value),
      })

      const cubes = computed(() => portfolioStore.cubes)

      const dayPrice = computed(
        () => portfolioStore.dayPrice?.toFixed(2) || 0
      )
      const dayConsumption = computed(
        () => portfolioStore.dayConsumption?.toFixed(1) || 0
      )
      const averagePrice = computed(() => {
        const average = (
          (100 * portfolioStore.dayPrice) /
          portfolioStore.dayConsumption
        ).toFixed(1)
        if (!average || isNaN(average)) return 0
        return average
      })

      const communitySharePercent = computed(() =>
        portfolioStore.communitySharePercent.toFixed(0)
      )

      const chartData = computed(() => portfolioStore.chartData)

      const getCubeNickName = (cubeName) => {
        const cube = mapStore.getCubeByName(cubeName, 'tour')
        if (cube) { return cube.params.nickName }
        return cubeName
      }

      return {
        loadProfile,
        consumption,
        cubes,
        loadProfiles,
        clpColor,
        priceColor,
        getPowerColor,
        dayPrice,
        dayConsumption,
        averagePrice,
        chartData,
        getCubeNickName,
        communitySharePercent,
        t,
      }
    },
  })
</script>

<style lang="scss" scoped>
  .portfolio {
    background-color: $white;
    padding: $spacer20 $spacer10 $spacer10;
    box-shadow: 2px 2px 7px rgba(0, 0, 0, 0.15);
  }

  .portfolio__group {
    margin-bottom: $spacer20;

    @media (max-width: $bp-lg - 1px) {
      margin: 0;
    }
  }

  .portfolio__input {
    border: none;
    border-bottom: 2px solid $grey-dark;
    border-radius: 0;
    height: 2.5rem;
    color: $grey-darker;
    font-size: 1em;
    padding: 0;
  }

  .portfolio__label {
    display: block;
    margin-bottom: 0;
    font-size: 0.9em;
    color: $grey-dark;
  }

  .portfolio__chart {
    margin: $spacer10 0 -#{$spacer05} 0;

    @media (max-width: $bp-lg - 1px) {
      display: none;
    }
  }

  .portfolio__list {
    display: flex;
    justify-content: space-around;
    margin-top: $spacer30;
    list-style: none;
    padding: 0;
    line-height: 1;

    > * + * {
      margin-left: $spacer20;
    }

    @media (max-width: $bp-lg - 1px) {
      margin: 0;
    }
  }

  .portfolio__list-number {
    display: block;
    font-size: 1.5em;
    font-weight: $font-weight-bold;
  }

  .portfolio__pills {
    display: flex;
    flex-wrap: wrap;
    gap: $spacer05;
    list-style: none;
    margin: $spacer20 0 0;
    padding: 0;

    @media (max-width: $bp-lg - 1px) {
      display: none;
    }
  }
</style>
